import { Injectable, signal, inject } from '@angular/core'
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http'
import { Observable, catchError, map } from 'rxjs'

import { environment } from '../../environments/environment'
import {
  Product,
  ProductData,
  ProductOverview,
  ProductAccess,
  ProductFilter,
  ProductFilterResult,
  CompanyProductFilterResult,
  AutoCompleteProductFilter,
  AutoCompleteProductFilterResult,
  ProductRegulation,
  ProductIngredientsResponse,
  CreateProduct,
  CanadianCategoryResponse,
  ProductCloningData,
  CSVImportResponse,
  CreateImportedProductsRequest,
  ProductPlasticsDeclarationResponse,
  ProductShareCreateRequest,
  ProductShareDeleteRequest,
  ProductShareListItem,
  BulkProductShareCsvValidateResponse,
  BulkCreateProductSharesRequest,
} from '../types/products'

import { ErrorService } from './error.service'
import { RootInjectorGuard } from '../guards/root-injector.guard'
import { ProductComponent } from '../types/chemistry'
import {
  FULL_INGREDIENT_DISCLOSURE,
  PARTIAL_INGREDIENT_DISCLOSURE,
} from '../constants/sustainability-highlights'
import { SustainabilityHighlight } from '../types/sustainabilityHighlight'
import { RecyclabilityLocation } from '../types/recyclability'
import { PlasticResin, ComponentForResinDerivation } from '../types/plastics'

@Injectable({
  providedIn: 'root',
})
export class ProductService extends RootInjectorGuard {
  private http = inject(HttpClient)
  private errorService = inject(ErrorService)

  private URL = environment.baseUrl + 'products'
  defaultProductData: ProductData = {
    id: '',
    name: '',
    brand: '',
    isCopiedFromSupplier: false,
  }
  currentProductData = signal(this.defaultProductData)

  constructor() {
    super(ProductService)
  }

  getAll(): Observable<Array<Product>> {
    return this.http
      .get<Array<Product>>(this.URL)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends request to create product
   * @param {CreateProduct} data The form data
   * @returns {Observable<ProductOverview>} Product
   */
  create(data: CreateProduct): Observable<Product> {
    return this.http
      .post<Product>(this.URL, data)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends request to edit product
   * @param {CreateProduct} data The form data
   * @param {string} id The id of the product to edit
   * @returns {Observable<ProductOverview>} Product
   */
  edit(data: CreateProduct, id: string): Observable<Product> {
    return this.http
      .put<Product>(this.URL + `/${id}`, data)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Checks whether the current user can view full product details
   * @param {string} id The id of the product
   * @returns {Observable<ProductAccess>} Product access for the current user
   */
  getAccess(id: string): Observable<ProductAccess> {
    return this.http
      .get<ProductAccess>(this.URL + `/${id}/access`)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends request to get a Product's Overview information
   * @param {string} id The id of the product
   * @param {RecyclabilityLocation} location The location the current user is viewing from
   * @returns {Observable<ProductOverview>} Product overview results
   */
  getOverview(
    id: string,
    location: RecyclabilityLocation,
  ): Observable<ProductOverview> {
    return this.http
      .get<ProductOverview>(this.URL + `/${id}/overview?location=${location}`)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends request to get a Product's Component information
   * @param {string} id The id of the product
   * @returns {Observable<ProductComponent>} Product chemistry results
   */
  getComponents(id: string): Observable<ProductComponent> {
    return this.http
      .get<ProductComponent>(this.URL + `/${id}/components`)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Retrieves product data for cloning or editing
   * @param {string} id The product id
   * @param {boolean} [editing=false] Whether to request the product for editing
   * @returns {Observable<ProductCloningData>} Product cloning data
   */
  getProductForCloning(
    id: string,
    editing = false,
  ): Observable<ProductCloningData> {
    return this.http
      .get<ProductCloningData>(`${this.URL}/${id}/clone`, {
        params: { editing },
      })
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Retrieves product categories
   * @param {string} keyword Keyword filter
   * @returns {Observable<CanadianCategoryResponse>} Product categories response
   */
  getCanadianCategories(keyword: string): Observable<CanadianCategoryResponse> {
    let params = new HttpParams()
    if (keyword != null) {
      params = params.set('keyword', keyword)
    }
    return this.http
      .get<CanadianCategoryResponse>(`${this.URL}/categories`, {
        params: params,
      })
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Calls the product filter API
   * @param {ProductFilter} filters Filters
   */
  filterProducts(filters: ProductFilter): Observable<ProductFilterResult> {
    let params = new HttpParams()
    if (filters.name != null) {
      params = params.set('name', filters.name)
    }
    if (filters.brand != null) {
      params = params.set('brand', filters.brand)
    }
    if (filters.batch != null) {
      params = params.set('batch', filters.batch)
    }
    if (filters.sku != null) {
      params = params.set('sku', filters.sku)
    }
    if (filters.serial != null) {
      params = params.set('serial', filters.serial)
    }
    if (filters.gs1Identifier != null) {
      params = params.set('gs1Identifier', filters.gs1Identifier)
    }
    if (filters.page != null) {
      params = params.set('page', filters.page)
    }
    if (filters.isForAddToCatalogue != null) {
      params = params.set('isForAddToCatalogue', filters.isForAddToCatalogue)
    }
    return this.http
      .get<ProductFilterResult>(`${this.URL}/filter`, { params: params })
      .pipe(catchError(this.errorService.handleError))
  }
  /**
   * Calls the autocomplete product filter API
   * @param {ProductFilter} filters Filters
   */
  autoCompleteProducts(
    filters: AutoCompleteProductFilter,
  ): Observable<AutoCompleteProductFilterResult> {
    let params = new HttpParams()
    if (filters.name != null) {
      params = params.set('name', filters.name)
    }
    return this.http
      .get<AutoCompleteProductFilterResult>(`${this.URL}/autocomplete`, {
        params: params,
      })
      .pipe(catchError(this.errorService.handleError))
  }
  /**
   * Sends request to get a Product's Chemistry information
   * @param {string} id The id of the product
   * @returns {Observable<Chemistry>} Product chemistry results
   */
  getRegulation(id: string): Observable<ProductRegulation> {
    return this.http
      .get<ProductRegulation>(this.URL + `/${id}/regulations`)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Retrieves products for the user's company or an authorized company view
   * @param {ProductFilter} filters Product filters and page
   * @param {string | null} [companyId] Company to view when authorized
   * @returns {Observable<CompanyProductFilterResult>} Product results
   */
  getCompanyProducts(
    filters: ProductFilter,
    companyId?: string | null,
  ): Observable<CompanyProductFilterResult> {
    let params = new HttpParams()
    if (companyId) {
      params = params.set('companyId', companyId)
    }
    if (filters.name != null) {
      params = params.set('name', filters.name)
    }
    if (filters.brand != null) {
      params = params.set('brand', filters.brand)
    }
    if (filters.batch != null) {
      params = params.set('batch', filters.batch)
    }
    if (filters.sku != null) {
      params = params.set('sku', filters.sku)
    }
    if (filters.serial != null) {
      params = params.set('serial', filters.serial)
    }
    if (filters.gs1Identifier != null) {
      params = params.set('gs1Identifier', filters.gs1Identifier)
    }
    if (filters.page != null) {
      params = params.set('page', filters.page)
    }

    return this.http
      .get<CompanyProductFilterResult>(this.URL + '/company-products?', {
        params,
      })
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Requests every owned product and its associations, independently of list filters and pagination.
   *
   * @returns {Observable<HttpResponse<Blob>>} The ZIP response, including the company-specific filename in its headers.
   */
  exportCompanyProducts(): Observable<HttpResponse<Blob>> {
    return this.http
      .get(`${this.URL}/company-products/export`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Retrieves most popular products
   * @returns {Observable<Array<ProductData>>} Product results
   */
  getPopularProducts(): Observable<Array<ProductData>> {
    return this.http
      .get<Array<ProductData>>(this.URL + '/popular')
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Retrieves ingredients associated with a product
   * @param {string} id The id of the product
   * @param {RecyclabilityLocation} location The location that the user is sending the request from
   * @returns {Observable<ProductIngredientsResponse>} Product Ingredient results
   */
  getProductIngredients(
    id: string,
    location: RecyclabilityLocation,
  ): Observable<ProductIngredientsResponse> {
    return this.http
      .get<ProductIngredientsResponse>(
        this.URL + `/${id}/ingredients?location=${location}`,
      )
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Returns an array of Sustainability Highlights for a Product
   * @param {ProductIngredientsResponse | ProductOverview} product Product data
   * @returns {Array<SustainabilityHighlight>} Array of product sustainability highlights
   */
  getSustainabilityHighlights(
    product: ProductIngredientsResponse | ProductOverview,
  ): Array<SustainabilityHighlight> {
    const sustainabilityHighlights: Array<SustainabilityHighlight> = []
    if (product.fullIngredientDisclosure) {
      sustainabilityHighlights.push(FULL_INGREDIENT_DISCLOSURE)
    } else {
      sustainabilityHighlights.push(FULL_INGREDIENT_DISCLOSURE)
      sustainabilityHighlights.push(PARTIAL_INGREDIENT_DISCLOSURE)
    }

    return sustainabilityHighlights
  }

  /**
   * Sends request to import products for review
   * @param {FormData} data The form data containing the CSV, template ID, and additional files
   * @returns {Observable<CSVImportResponse>} Response from the backend
   */
  importProductsForReview(data: FormData): Observable<CSVImportResponse> {
    return this.http
      .post<CSVImportResponse>(this.URL + '/import', data)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends request to create imported products
   * @param {CreateImportedProductsRequest} products Products to create
   * @returns {Observable<void>} Response from the backend
   */
  createImportedProducts(
    request: CreateImportedProductsRequest,
  ): Observable<void> {
    return this.http
      .post<void>(this.URL + '/confirm-import', request)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Retrieves plastics declaration associated with a product
   * @param {string} id The id of the product
   * @returns {Observable<ProductPlasticsDeclarationResponse>} Product plastic declaration results
   */
  getProductPlasticsDeclaration(
    id: string,
  ): Observable<ProductPlasticsDeclarationResponse> {
    return this.http
      .get<ProductPlasticsDeclarationResponse>(
        this.URL + `/${id}/plastics-declaration`,
      )
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Adds an external product to a company's catalogue
   * @param {string} id The id of the product to copy
   * @returns {Observable<void>} Emits when the product is added (201 Created, empty body)
   */
  addProductToCatalogue(id: string): Observable<void> {
    return this.http
      .post<void>(`${this.URL}/${id}/add-to-catalogue`, {})
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Updates the discontinued status of a product
   * @param {string} id The id of the product
   * @param {boolean} discontinued The discontinued status to set
   * @param {number | undefined} discontinuedYear The last reporting year for this product (optional)
   * @returns {Observable<void>} Emits when the update completes
   */
  updateDiscontinuedStatus(
    id: string,
    discontinued: boolean,
    discontinuedYear?: number,
  ): Observable<void> {
    const body: { discontinued: boolean; discontinuedYear?: number } = {
      discontinued,
    }

    if (discontinuedYear != null) {
      body.discontinuedYear = discontinuedYear
    }

    return this.http
      .put<void>(`${this.URL}/${id}/discontinued`, body)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Calculates aggregated plastic resins from the given components.
   * This calls POST /products/plastics/component-resins and returns
   * a flat, deduplicated list of resins.
   *
   * We send a simplified payload to the backend containing only the fields
   * it actually needs for the calculation:
   *   - componentId
   *   - quantity
   *   - groupIndex (optional, used for ABOM when components are interchangeable)
   *
   * @param {ComponentForResinDerivation[]} components The components to calculate from
   * @returns {Observable<PlasticResin[]>} Aggregated resins
   */
  getPlasticResinsFromComponents(
    components: ComponentForResinDerivation[],
  ): Observable<PlasticResin[]> {
    const body = { components }

    return this.http
      .post<{
        plasticResins: PlasticResin[]
      }>(`${this.URL}/plastics/component-resins`, body)
      .pipe(
        map((response) => response.plasticResins),
        catchError(this.errorService.handleError),
      )
  }

  /**
   * Shares a product with one or more connected companies.
   * @param {ProductShareCreateRequest} request Share request payload
   * @returns {Observable<void>}
   */
  shareProduct(request: ProductShareCreateRequest): Observable<void> {
    const url = `${this.URL}/shares`
    return this.http
      .post<void>(url, request)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Revokes a product share for a specific recipient company.
   * @param {string} productId The product id to revoke sharing for
   * @param {ProductShareDeleteRequest} request Revoke request payload (recipient only)
   * @returns {Observable<void>}
   */
  revokeProductShare(
    productId: string,
    request: ProductShareDeleteRequest,
  ): Observable<void> {
    const url = `${this.URL}/${productId}/shares`
    return this.http
      .delete<void>(url, { body: request })
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Returns the companies a product is currently shared with.
   * @param {string} productId Product identifier
   * @returns {Observable<ProductShareListItem[]>} Current product shares
   */
  getProductShares(productId: string): Observable<ProductShareListItem[]> {
    return this.http
      .get<ProductShareListItem[]>(`${this.URL}/${productId}/shares`)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Uploads a CSV and returns a row-by-row preview of what will be shared.
   * @param {FormData} data Multipart form data containing `template` and the CSV file
   * @returns {Observable<BulkProductShareCsvValidateResponse>} Preview rows and validation flags
   */
  previewBulkProductShares(
    data: FormData,
  ): Observable<BulkProductShareCsvValidateResponse> {
    return this.http
      .post<BulkProductShareCsvValidateResponse>(
        `${this.URL}/bulk-share/preview`,
        data,
      )
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Creates product share records in bulk based on the user's confirmed preview.
   * @param {BulkCreateProductSharesRequest} request Confirmed bulk share payload
   * @returns {Observable<void>}
   */
  createBulkProductShares(
    request: BulkCreateProductSharesRequest,
  ): Observable<void> {
    return this.http
      .post<void>(`${this.URL}/bulk-share/confirm`, request)
      .pipe(catchError(this.errorService.handleError))
  }
}
